import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, HelpCircle, RefreshCw, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

const STATUS_ICON = {
  ok:     <CheckCircle2  className="w-4 h-4 text-[#10b981]" />,
  fail:   <XCircle       className="w-4 h-4 text-[#ef4444]" />,
  warn:   <AlertTriangle className="w-4 h-4 text-[#f59e0b]" />,
  manual: <HelpCircle    className="w-4 h-4 text-[#3b82f6]" />,
};

const OVERALL_STYLE = {
  healthy: { c: "#10b981", label: "Healthy", desc: "All execution gates green — signals will place live trades." },
  warn:    { c: "#f59e0b", label: "Attention", desc: "Trades can fire but one or more gates need review." },
  blocked: { c: "#ef4444", label: "Blocked",   desc: "One or more required gates are OFF — no live trades will be placed." },
};

// Dashboard 'Trade Execution Health' card — single source of truth for
// diagnosing "EA connected but nothing trades" support cases.
export default function ExecutionHealthCard({ accountId, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [ackedEaAutoExec, setAckedEaAutoExec] = useState(
    () => localStorage.getItem("veerox_ack_ea_auto_execute") === "1"
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const q = accountId ? `?account_id=${accountId}` : "";
      const r = await api.get(`/dashboard/execution-health${q}`);
      setData(r.data);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const toggleAutoExecute = async (enabled) => {
    try {
      const q = accountId ? `?account_id=${accountId}` : "";
      await api.post(`/dashboard/auto-execute${q}`, { enabled });
      toast.success(enabled ? "Auto-Execute ENABLED — approved signals will place live trades." : "Auto-Execute disabled — signals will remain advisory-only.");
      // Notify any listener (top-bar pill) so it can refresh instantly instead
      // of waiting for its 15-s poll tick.
      window.dispatchEvent(new CustomEvent("veerox:auto-execute-changed", { detail: { enabled } }));
      load();
      onChange?.();
    } catch (e) {
      toast.error("Failed to toggle Auto-Execute");
    }
  };

  const ackEaAutoExecute = (checked) => {
    localStorage.setItem("veerox_ack_ea_auto_execute", checked ? "1" : "0");
    setAckedEaAutoExec(checked);
  };

  if (!data) {
    if (loadError) {
      return (
        <div className="vx-card p-5 border-[#ef4444]/40 bg-[#ef4444]/5" data-testid="exec-health-card-error">
          <div className="vx-label mb-2 text-[#ef4444]">Trade Execution Health · error</div>
          <div className="text-xs text-[#9ca3af] font-mono mb-3">{String(loadError)}</div>
          <button
            onClick={load}
            data-testid="exec-health-retry"
            className="h-8 px-3 rounded border border-[#1f2937] hover:bg-[#1f2937] text-xs font-mono flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Retry
          </button>
        </div>
      );
    }
    return (
      <div className="vx-card p-5" data-testid="exec-health-card-loading">
        <div className="vx-label mb-2">Trade Execution Health</div>
        <div className="text-xs text-[#6b7280] font-mono">Loading…</div>
      </div>
    );
  }

  const overall = OVERALL_STYLE[data.overall] || OVERALL_STYLE.warn;

  return (
    <div className="vx-card p-5 space-y-4" data-testid="exec-health-card">
      {/* Header + overall status */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 vx-label">
            <Zap className="w-3.5 h-3.5" /> Trade Execution Health
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <span
              data-testid="exec-health-overall"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono font-bold border"
              style={{ color: overall.c, borderColor: overall.c, background: `${overall.c}22` }}
            >
              ● {overall.label.toUpperCase()}
            </span>
            <span className="text-xs text-[#9ca3af]">{overall.desc}</span>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="exec-health-refresh"
          className="h-8 px-2.5 rounded border border-[#1f2937] hover:bg-[#1f2937] text-[10px] font-mono flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Prominent auto-execute toggle */}
      <div
        className={`rounded border p-3 flex items-center justify-between gap-4 ${
          data.auto_execute
            ? "border-[#10b981]/50 bg-[#10b981]/5"
            : "border-[#ef4444]/50 bg-[#ef4444]/5"
        }`}
      >
        <div>
          <div className="text-sm font-semibold flex items-center gap-2">
            Auto-Execute (server-side)
            {data.auto_execute ? (
              <span className="text-[10px] font-mono font-bold text-[#10b981]" data-testid="auto-exec-status">
                ✓ ON · signals place trades
              </span>
            ) : (
              <span className="text-[10px] font-mono font-bold text-[#ef4444]" data-testid="auto-exec-status">
                ⚠ OFF · trades WILL NOT execute
              </span>
            )}
          </div>
          <div className="text-[10px] text-[#9ca3af] font-mono mt-0.5">
            When OFF, the EA logs signals but never calls OrderSend. Must be ON to trade live.
          </div>
        </div>
        <Switch
          checked={!!data.auto_execute}
          onCheckedChange={toggleAutoExecute}
          data-testid="auto-exec-toggle"
        />
      </div>

      {/* Checklist */}
      <div className="space-y-2" data-testid="exec-health-checklist">
        {data.checks.map((c) => {
          const isEaAck = c.id === "ea_auto_execute";
          // Visually mark EA-side ack as ok when user has confirmed via checkbox
          const effectiveStatus = isEaAck && ackedEaAutoExec ? "ok" : c.status;
          return (
            <div key={c.id} className="rounded border border-[#1f2937] bg-[#0a0e1a] p-3" data-testid={`exec-health-check-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="mt-0.5">{STATUS_ICON[effectiveStatus] || STATUS_ICON.warn}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{c.label}</div>
                  <div className="text-[11px] font-mono text-[#9ca3af] mt-0.5 break-words">{c.detail}</div>
                  {c.status !== "ok" && c.help && (
                    <div className="text-[10px] text-[#6b7280] italic mt-1">{c.help}</div>
                  )}
                </div>
                {c.id === "server_auto_execute" && !data.auto_execute && (
                  <button
                    onClick={() => toggleAutoExecute(true)}
                    data-testid="fix-auto-exec-btn"
                    className="h-7 px-2.5 rounded bg-[#10b981] hover:bg-[#059669] text-[10px] font-bold uppercase tracking-wide"
                  >
                    Fix
                  </button>
                )}
                {isEaAck && (
                  <label className="flex items-center gap-1.5 cursor-pointer" data-testid="ack-ea-auto-execute-label">
                    <input
                      type="checkbox"
                      checked={ackedEaAutoExec}
                      onChange={(e) => ackEaAutoExecute(e.target.checked)}
                      data-testid="ack-ea-auto-execute-checkbox"
                      className="accent-[#3b82f6]"
                    />
                    <span className="text-[10px] font-mono text-[#9ca3af]">I've enabled it in MT5</span>
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
